export class Sfx {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;

  private ensure(): AudioContext | null {
    if (typeof window === 'undefined') {
      return null;
    }
    if (!this.context) {
      const ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!ctor) {
        return null;
      }
      this.context = new ctor();
      this.master = this.context.createGain();
      this.master.gain.value = 0.5;
      this.master.connect(this.context.destination);
    }
    if (this.context.state === 'suspended') {
      void this.context.resume();
    }
    return this.context;
  }

  resume(): void {
    this.ensure();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.master) {
      this.master.gain.value = this.muted ? 0 : 0.5;
    }
    return this.muted;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  private tone(
    type: OscillatorType,
    frequency: number,
    delay: number,
    duration: number,
    gain: number,
    glideTo?: number,
  ): void {
    const context = this.ensure();
    if (!context || !this.master || this.muted) {
      return;
    }
    const start = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    if (glideTo !== undefined) {
      oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, glideTo), start + duration);
    }
    envelope.gain.setValueAtTime(0.0001, start);
    envelope.gain.exponentialRampToValueAtTime(gain, start + 0.012);
    envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(envelope);
    envelope.connect(this.master);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.03);
  }

  hit(): void {
    this.tone('sine', 880, 0, 0.16, 0.5);
    this.tone('sine', 1320, 0.012, 0.12, 0.16);
  }

  doubleHit(): void {
    this.tone('sine', 880, 0, 0.14, 0.45);
    this.tone('sine', 1174.66, 0.075, 0.16, 0.42);
    this.tone('sine', 1567.98, 0.15, 0.24, 0.4);
  }

  miss(): void {
    this.tone('triangle', 196, 0, 0.22, 0.45, 138);
  }

  gameOver(): void {
    this.tone('sine', 659.25, 0, 0.24, 0.34);
    this.tone('sine', 523.25, 0.16, 0.26, 0.32);
    this.tone('sine', 415.3, 0.32, 0.3, 0.3);
    this.tone('sine', 329.63, 0.5, 0.5, 0.28);
  }

  doodle(): void {
    this.tone('sine', 1318.51, 0, 0.14, 0.24);
    this.tone('sine', 1760, 0.07, 0.24, 0.22);
  }

  start(): void {
    this.tone('sine', 523.25, 0, 0.12, 0.3);
    this.tone('sine', 783.99, 0.08, 0.2, 0.3);
  }
}

export const sfx = new Sfx();
